/**
 * Settings module for accessing environment variables with type safety
 * Uses import.meta.env to access Vite environment variables
 *
 * Usage examples:
 *
 * // 1) As a composable
 * import { useSettings } from '@/composables/useSettings'
 * const settings = useSettings()
 *
 * const apiUrl = settings.getString('VITE_API_URL')
 * const debugMode = settings.getBoolean('VITE_DEBUG_MODE')
 * const maxRetries = settings.getNumber('VITE_MAX_RETRIES')
 *
 * if (settings.has('VITE_FEATURE_FLAG')) {
 *   const isEnabled = settings.getBoolean('VITE_FEATURE_FLAG')
 * }
 *
 * // 2) Using direct functions
 * import { getString, getNumber, getBoolean } from '@/composables/useSettings'
 *
 * const api = getString('VITE_API_URL')
 * const timeoutMs = getNumber('VITE_TIMEOUT_MS')
 * const enableAnalytics = getBoolean('VITE_ENABLE_ANALYTICS')
 *
 * // 3) Suggested typings in env.d.ts for stronger type safety
 * // interface ImportMetaEnv {
 * //   readonly VITE_API_URL: string
 * //   readonly VITE_DEBUG_MODE: string
 * //   readonly VITE_MAX_RETRIES: string
 * // }
 *
 * // Notes:
 * // - In Vite, only variables prefixed with VITE_ are exposed to client code.
 */

// All settings are required; no options supported

/**
 * Get a string setting from environment variables
 * @param name - The name of the environment variable
 * @param defaultValue - The default value to return if the setting is not defined
 * @returns The string value or default value
 * @throws Error if required setting is not found and no default is provided
 */
export function getStringSetting(name: string, defaultValue?: string): string {
  const value = import.meta.env[name];
  if (value === undefined || value === null || String(value).trim() === '') {
    if (defaultValue) {
      return defaultValue;
    }
    throw new Error(`Required setting '${name}' is not defined`);
  }
  return String(value);
}

/**
 * Get a number setting from environment variables
 * @param name - The name of the environment variable
 * @param defaultValue - The default value to return if the setting is not defined
 * @returns The number value or default value
 * @throws Error if required setting is not found and no default is provided, or if value cannot be parsed as number
 */
export function getNumberSetting(name: string, defaultValue?: number): number {
  const value = import.meta.env[name];
  if (value === undefined || value === null || String(value).trim() === '') {
    if (defaultValue) {
      return defaultValue;
    }
    throw new Error(`Required setting '${name}' is not defined`);
  }
  const parsed = Number(value);
  if (isNaN(parsed)) {
    throw new Error(`Setting '${name}' cannot be parsed as a number. Value: '${value}'`);
  }
  return parsed;
}

/**
 * Get a boolean setting from environment variables
 * @param name - The name of the environment variable
 * @param defaultValue - The default value to return if the setting is not defined
 * @returns The boolean value or default value
 * @throws Error if required setting is not found and no default is provided
 */
export function getBooleanSetting(name: string, defaultValue?: boolean): boolean {
  const value = import.meta.env[name];
  if (value === undefined || value === null || String(value).trim() === '') {
    if (defaultValue) {
      return defaultValue;
    }
    throw new Error(`Required setting '${name}' is not defined`);
  }
  const lowerValue = String(value).toLowerCase();
  return lowerValue === 'true' || lowerValue === '1' || lowerValue === 'yes' || lowerValue === 'on';
}

/**
 * Check if a setting exists in environment variables
 * @param name - The name of the environment variable
 * @returns True if the setting exists, false otherwise
 */
export function hasSetting(name: string): boolean {
  return import.meta.env[name] !== undefined && import.meta.env[name] !== null;
}

/**
 * Get all available environment variables
 * @returns Object containing all environment variables
 */
export function getAllSettings(): Record<string, string> {
  return { ...import.meta.env };
}

/**
 * Composable for accessing settings with reactive updates
 * Note: Environment variables are static at build time, so reactivity is limited
 */
export function useSettings() {
  return {
    getString: getStringSetting,
    getNumber: getNumberSetting,
    getBoolean: getBooleanSetting,
    has: hasSetting,
    getAll: getAllSettings,
  };
}

// Export individual functions for direct use
export {
  getStringSetting as getString,
  getNumberSetting as getNumber,
  getBooleanSetting as getBoolean,
};
